import { Link, useLocation } from 'react-router-dom'
import {
  LogOut,
  LayoutDashboard,
  Settings,
  Users as UsersIcon,
  UserCheck,
  History,
  MailSearch,
  Wrench,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useMailReviewAccess } from '../features/mailReview/useMailReviewAccess'
import { isOwnerOnly } from '../features/mailReview/access'

const menuConfig = {
  'dashboard': { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  'leads': { name: 'Leads', href: '/leads', icon: UserCheck },
  'lead-log': { name: 'Lead Log', href: '/lead-log', icon: History },
  'mail-review': { name: 'Mail Review', href: '/mail-review', icon: MailSearch },
  'maintenance': { name: 'Maintenance', href: '/maintenance', icon: Wrench },
  'users': { name: 'Users', href: '/users', icon: UsersIcon },
  'settings': { name: 'Settings', href: '/settings', icon: Settings },
}

const menuStructure = [
  {
    type: 'items',
    keys: [
      'dashboard',
      'leads',
      'lead-log',
      'mail-review',
      'maintenance',
      'users',
      'settings',
    ],
  },
]

export default function Sidebar({ children }) {
  const { user, logout, canManageUsers } = useAuthStore()
  const canAccessMailReview = useMailReviewAccess()

  const sections = buildSections(user, canManageUsers, canAccessMailReview)

  return (
    <div className="flex flex-col h-full min-h-0 bg-white border-r border-gray-200">
      <div className="flex h-16 flex-shrink-0 items-center px-4">
        <h1 className="text-xl font-bold text-gray-900">Ginger CMS</h1>
      </div>

      <div className="sidebar-scroll flex-1 min-h-0 overflow-y-auto">
        {children || <Navigation sections={sections} />}
      </div>

      <div className="flex-shrink-0 border-t border-gray-200 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-700">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
            {user?.roles && (
              <p className="text-xs text-primary-600">
                {user.roles[0]?.name?.charAt(0).toUpperCase() +
                  user.roles[0]?.name?.slice(1)}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={logout}
          className="mt-3 flex w-full items-center px-2 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  )
}

function buildSections(user, canManageUsers, canAccessMailReview) {
  const menuPermissions = user?.menu_permissions || []

  if (isOwnerOnly(user)) {
    return canAccessMailReview
      ? [{ type: 'items', items: [menuConfig['mail-review']] }]
      : []
  }

  const isAllowed = (key) => {
    if (key === 'lead-log') {
      return !menuPermissions.length || menuPermissions.includes('leads')
    }
    if (key === 'mail-review') return canAccessMailReview
    if (key === 'users')
      return (
        (!menuPermissions.length || menuPermissions.includes(key)) &&
        canManageUsers()
      )
    if (key === 'settings') return true
    return !menuPermissions.length || menuPermissions.includes(key)
  }

  const sections = []

  for (const section of menuStructure) {
    const items = section.keys.filter(isAllowed).map((key) => menuConfig[key])

    if (items.length === 0) continue

    if (section.type === 'group') {
      sections.push({ type: 'group', label: section.label, items })
    } else {
      sections.push({ type: 'items', items })
    }
  }

  return sections
}

export function Navigation({ items, sections }) {
  const location = useLocation()
  const { user, canManageUsers } = useAuthStore()
  const canAccessMailReview = useMailReviewAccess()

  // Backwards compatibility: if items prop is passed directly
  if (items && !sections) {
    sections = [{ type: 'items', items }]
  }

  // Self-sufficient: build sections when called with no props (e.g. MobileSidebar)
  if (!sections) {
    sections = buildSections(user, canManageUsers, canAccessMailReview)
  }

  return (
    <nav className="px-2 py-4 space-y-1">
      {sections.map((section, sectionIdx) => (
        <div key={sectionIdx}>
          {section.type === 'group' && (
            <div className="px-2 pt-4 pb-1 first:pt-0">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                {section.label}
              </span>
            </div>
          )}
          {section.items.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/dashboard' &&
                location.pathname.startsWith(item.href + '/'))
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive
                    ? 'bg-primary-100 text-primary-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            )
          })}
        </div>
      ))}
    </nav>
  )
}
